import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from '../Header';
import Sidebar from '../Sidebar';
import DateInput from '../components/DateInput';

const API_URL = process.env.REACT_APP_API_URL;

interface Consultant {
  id: number;
  name: string;
  surname: string;
  email?: string;
}

interface DriverLicenseEntry {
  code: string;
  dateOfFirstIssue: string;
  expiresOn: string;
}

const AddClientPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [formData, setFormData] = useState({
    title: '',
    name: '',
    middle_names: '',
    surname: '',
    nickname: '',
    date_of_birth: '',
    gender: '',
    marital_status: '',
    status: 'Draft',
    language: '',
    id_passport_number: '',
    emails: [] as string[],
    mobile_number: '+27',
    organization: '',
    mobile_no: '',
    telephone_number: '+27',
    tax_number: '',
    employer_name: '',
    job_description: '',
    consultant: '',
    address: '',
    personal: '',
    commercial: false,
    client_number: '',
    drivers_licenses: [] as DriverLicenseEntry[],
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [mobileError, setMobileError] = useState<string | null>(null);
  const [telephoneError, setTelephoneError] = useState<string | null>(null);
  const [idPassportError, setIdPassportError] = useState<string | null>(null);
  const [availableLicenseCodes] = useState([
    'A', 'A1', 'B', 'C', 'C1', 'D', 'D1', 'EB', 'EC', 'EC1', 'ED', 'ED1'
  ]);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [languages, setLanguages] = useState<{ id: number; name: string; is_default: boolean }[]>([]);
  const [loadingLanguages, setLoadingLanguages] = useState(false);
  const [brokerDetails, setBrokerDetails] = useState<{ companyName: string } | null>(null);
    const [brokers, setBrokers] = useState<{ id: number; company_name: string }[]>([]);
  const [selectedBrokerId, setSelectedBrokerId] = useState<string>('');
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const addEmail = () => {
    const trimmedEmail = emailInput.trim();
    if (!trimmedEmail) {
      setEmailError('Email cannot be empty');
      return;
    }
    if (!validateEmail(trimmedEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    if (formData.emails.includes(trimmedEmail)) {
      setEmailError('Email already added');
      return;
    }
    setFormData(prev => ({
      ...prev,
      emails: [...prev.emails, trimmedEmail]
    }));
    setEmailInput('');
    setEmailError(null);
  };

  const removeEmail = (index: number) => {
    setFormData(prev => ({
      ...prev,
      emails: prev.emails.filter((_, i) => i !== index)
    }));
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmail();
    }
  };

  const fetchLanguages = async () => {
    setLoadingLanguages(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/languages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success && Array.isArray(response.data.data)) {
        setLanguages(response.data.data);
      } else {
        setLanguages([]);
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
      setLanguages([]);
    } finally {
      setLoadingLanguages(false);
    }
  };

  const fetchBrokerDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/broker-details`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        const companyName = data.companyName || data.company_name;
        setBrokerDetails({ companyName });
      }
    } catch (error) {
      console.error('Error fetching broker details:', error);
    }
  };

  const getAvailableLicenseCodes = (currentIndex: number) => {
    const currentSelectedCodes = formData.drivers_licenses
      .map(license => license.code)
      .filter(code => code !== '');
    return availableLicenseCodes.filter(code => {
      const selectedInOtherDropdowns = currentSelectedCodes.filter(
        (selectedCode, index) => index !== currentIndex && selectedCode === code
      );
      return selectedInOtherDropdowns.length === 0;
    });
  };

  const canAddMoreLicenses = () => {
    const availableCodes = getAvailableLicenseCodes(formData.drivers_licenses.length);
    return availableCodes.length > 0;
  };

  const addDriverLicense = () => {
    const availableCodes = getAvailableLicenseCodes(formData.drivers_licenses.length);
    const availableCode = availableCodes[0] || '';
    setFormData(prev => ({
      ...prev,
      drivers_licenses: [
        ...prev.drivers_licenses,
        { code: availableCode, dateOfFirstIssue: '', expiresOn: '' }
      ]
    }));
  };

  const removeDriverLicense = (index: number) => {
    setFormData(prev => ({
      ...prev,
      drivers_licenses: prev.drivers_licenses.filter((_, i) => i !== index)
    }));
  };

  const handleDriverLicenseChange = (index: number, field: keyof DriverLicenseEntry, value: string) => {
    setFormData(prev => ({
      ...prev,
      drivers_licenses: prev.drivers_licenses.map((license, i) =>
        i === index ? { ...license, [field]: value } : license
      )
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (name === 'id_passport_number') {
      const cleanedValue = value.replace(/[^0-9]/g, '');
      if (cleanedValue.length !== 13 && cleanedValue.length > 0) {
        setIdPassportError('ID/Passport number must be exactly 13 digits');
      } else {
        setIdPassportError(null);
      }
      setFormData((prev) => ({ ...prev, [name]: cleanedValue }));
    } else if (name === 'mobile_number') {
      let cleanedValue = value;
      if (!cleanedValue.startsWith('+27')) {
        // Strip leading +27 or 27, then strip a leading 0 (SA trunk prefix),
        // then strip all non-digits, then prepend +27
        cleanedValue = '+27' + cleanedValue
          .replace(/^(\+27|27)/, '')
          .replace(/[^0-9]/g, '')
          .replace(/^0+/, ''); // 👈 NEW: strip any leading zeros
      } else {
        cleanedValue = '+27' + cleanedValue
          .slice(3)
          .replace(/[^0-9]/g, '')
          .replace(/^0+/, ''); // 👈 NEW: same here
      }
      const digitsOnly = cleanedValue.slice(3);
      if (digitsOnly.length > 10) {
        setMobileError('Mobile number cannot exceed 10 digits');
        return;
      } else {
        setMobileError(null);
      }
      setFormData((prev) => ({ ...prev, [name]: cleanedValue }));
    } else if (name === 'telephone_number') {
      let cleanedValue = value;
      if (!cleanedValue.startsWith('+27')) {
        cleanedValue = '+27' + cleanedValue
          .replace(/^(\+27|27)/, '')
          .replace(/[^0-9]/g, '')
          .replace(/^0+/, '');
      } else {
        cleanedValue = '+27' + cleanedValue
          .slice(3)
          .replace(/[^0-9]/g, '')
          .replace(/^0+/, '');
      }
      const digitsOnly = cleanedValue.slice(3);
      if (digitsOnly.length > 10) {
        setTelephoneError('Telephone number cannot exceed 10 digits');
        return;
      } else {
        setTelephoneError(null);
      }
      setFormData((prev) => ({ ...prev, [name]: cleanedValue }));
    } else if (name === 'language') {
      const selectedLanguage = languages.find(lang => lang.id.toString() === value);
      setFormData((prev) => ({ ...prev, [name]: selectedLanguage ? selectedLanguage.name : value }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (mobileError || idPassportError) {
      setFormError('Please correct the errors in the form.');
      return;
    }

    if (formData.emails.length === 0) {
      setFormError('Please add at least one email address.');
      return;
    }

    for (const email of formData.emails) {
      if (!validateEmail(email)) {
        setFormError(`Invalid email format: ${email}`);
        return;
      }
    }

    const invalidLicenses = formData.drivers_licenses.filter(
      license => license.code && (!license.dateOfFirstIssue || !license.expiresOn)
    );

    if (invalidLicenses.length > 0) {
      setFormError('Please provide both Date of First Issue and Expires On for all selected license codes.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const prefix = brokerDetails?.companyName
        ? brokerDetails.companyName.substring(0, 3).toUpperCase()
        : 'MNH';

      const clientsResponse = await axios.get(`${API_URL}/api/clients?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const newFormatRegex = new RegExp(`^${prefix}\\d{5}$`);
      const clientsWithNewFormat = clientsResponse.data.filter((client: any) =>
        client.client_number && newFormatRegex.test(client.client_number)
      );

      let maxSequence = 0;
      clientsWithNewFormat.forEach((client: any) => {
        if (client.client_number) {
          const numberPart = client.client_number.substring(prefix.length);
          const sequenceNum = parseInt(numberPart, 10);
          if (!isNaN(sequenceNum) && sequenceNum > maxSequence) {
            maxSequence = sequenceNum;
          }
        }
      });

      const nextSequence = maxSequence + 1;
      const paddedSequence = nextSequence.toString().padStart(5, '0');
      const newClientNumber = `${prefix}${paddedSequence}`;

      const primaryEmail = formData.emails[0];

      const submissionData: Record<string, any> = {
        title: formData.title,
        name: formData.name,
        middle_names: formData.middle_names,
        surname: formData.surname,
        nickname: formData.nickname,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender,
        marital_status: formData.marital_status,
        status: formData.status,
        language: formData.language,
        id_passport_number: formData.id_passport_number,
        emails: formData.emails,
        email: primaryEmail,
        mobile_number: formData.mobile_number,
        telephone_number: formData.telephone_number,
        tax_number: formData.tax_number,
        employer_name: formData.employer_name,
        job_description: formData.job_description,
        consultant: formData.consultant,
        address: formData.address,
        personal: formData.personal,
        commercial: formData.commercial,
        client_number: newClientNumber,
        drivers_license_codes: formData.drivers_licenses
          .filter(license => license.code)
          .map(license => `${license.code}|${license.dateOfFirstIssue}|${license.expiresOn}`)
          .join(',')
      };

      await axios.post(`${API_URL}/api/clients`, submissionData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      navigate('/clients');

    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 400) {
          setFormError(`Database error: ${err.response.data.detail}`);
        } else {
          setFormError('Failed to add client. Please try again.');
        }
      } else {
        setFormError('Failed to add client. Please try again.');
      }
    }
  };

  useEffect(() => {
        const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);

        const u = response.data;
        const isSA =
          u?.is_super_admin === true ||
          u?.isSuperAdmin === true ||
          u?.role === 'super_admin' ||
          u?.role === 'superadmin' ||
          (u?.role === 'admin' && (u?.broker_id == null || u?.broker_id === ''));
        setIsSuperAdmin(isSA);
      } catch (error) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    };

    const fetchBrokers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/brokers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = response.data?.data || response.data || [];
        setBrokers(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error('Error fetching brokers:', error);
        setBrokers([]);
      }
    };

    const fetchConsultants = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/consultants`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success && Array.isArray(response.data.data)) {
          setConsultants(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching consultants:', error);
      }
    };

    fetchUser();
    fetchConsultants();
    fetchLanguages();
    fetchBrokerDetails();
    fetchBrokers();
  }, [navigate]);

  if (!user) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title="Add New Client"
          user={user}
          toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={true}
        />
        <div className="flex-1 p-6">
          <div className="max-w-5xl mx-auto">
            <button
              onClick={() => navigate('/clients')}
              className="mb-4 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            >
              ← Back to Clients
            </button>
            <div className="bg-white rounded-xl p-8 shadow-2xl">
              <h3 className="text-lg font-semibold text-gray-800 mb-6">Add New Client</h3>
              {formError && <p className="text-red-500 mb-4">{formError}</p>}

              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Title and First Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 mb-1 font-medium">Title</label>
                    <select
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                      required
                    >
                      <option value="">Select Title</option>
                      <option value="Mr">Mr</option>
                      <option value="Mrs">Mrs</option>
                      <option value="Miss">Miss</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">First Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                      required
                    />
                  </div>
                </div>

                {/* Middle Names and Surname */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Middle Name/s</label>
                    <input
                      type="text"
                      name="middle_names"
                      value={formData.middle_names}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Surname</label>
                    <input
                      type="text"
                      name="surname"
                      value={formData.surname}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                    />
                  </div>
                </div>

                {/* Nickname and Date of Birth */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Nickname/s</label>
                    <input
                      type="text"
                      name="nickname"
                      value={formData.nickname}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Date of Birth</label>
                    <DateInput
                        value={formData.date_of_birth}
                        onChange={(date) => {
                        const syntheticEvent = {
                            target: {
                            name: 'date_of_birth',
                            value: date
                            }
                        } as React.ChangeEvent<HTMLInputElement>;
                        handleInputChange(syntheticEvent);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all bg-gray-50"
                    />
                    </div>

                </div>

                {/* Gender and Marital Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Gender</label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                      required
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Marital Status</label>
                    <select
                      name="marital_status"
                      value={formData.marital_status}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                    >
                      <option value="">Select Marital Status</option>
                      <option value="Married">Married</option>
                      <option value="Single">Single</option>
                      <option value="Divorced">Divorced</option>
                      <option value="Widow">Widow</option>
                    </select>
                  </div>
                </div>

                {/* Language and ID/Passport */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Language</label>
                    <select
                      name="language"
                      value={languages.find(lang => lang.name === formData.language)?.id || ''}
                      onChange={(e) => {
                        const selectedLanguage = languages.find(lang => lang.id.toString() === e.target.value);
                        setFormData(prev => ({
                          ...prev,
                          language: selectedLanguage ? selectedLanguage.name : ''
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                    >
                      <option value="">Select Language</option>
                      {languages.map((lang) => (
                        <option key={lang.id} value={lang.id}>
                          {lang.name}
                          {lang.is_default && ' (Default)'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">ID/Passport Number</label>
                    <input
                      type="text"
                      name="id_passport_number"
                      value={formData.id_passport_number}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${idPassportError ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50`}
                    />
                    {idPassportError && <p className="text-red-500 text-sm mt-1">{idPassportError}</p>}
                  </div>
                </div>

                {/* Mobile and Telephone Numbers */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Mobile Number</label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-100 text-gray-600 text-sm">
                        +27
                      </span>
                      <input
                        type="tel"
                        name="mobile_number"
                        value={formData.mobile_number.replace('+27', '')}
                        onChange={handleInputChange}
                        className={`flex-1 px-3 py-2 border ${mobileError ? 'border-red-500' : 'border-gray-300'} rounded-r-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white`}
                        placeholder="Enter 10-digit number"
                        maxLength={10}
                      />
                    </div>
                    {mobileError && <p className="text-red-500 text-sm mt-1">{mobileError}</p>}
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Telephone Number</label>
                    <input
                      type="text"
                      name="telephone_number"
                      value={formData.telephone_number}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                    />
                    {telephoneError && <p className="text-red-500 text-sm mt-1">{telephoneError}</p>}
                  </div>
                </div>

                {/* Tax Number and Employer Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Tax Number</label>
                    <input
                      type="text"
                      name="tax_number"
                      value={formData.tax_number}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Employer Name</label>
                    <input
                      type="text"
                      name="employer_name"
                      value={formData.employer_name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                    />
                  </div>
                </div>

                {/* Job Description and Consultant */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Job Description</label>
                    <input
                      type="text"
                      name="job_description"
                      value={formData.job_description}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Consultant</label>
                    <select
                      name="consultant"
                      value={formData.consultant}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                      required
                    >
                      <option value="">Select a Consultant</option>
                      {consultants.map((consultant) => (
                        <option key={consultant.id} value={consultant.id}>
                          {consultant.name} {consultant.surname}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Status and Email Fields */}
                <div className="grid grid-cols-5 gap-4">
                  <div className="col-span-2">
                    <label className="block text-gray-700 font-medium mb-2">Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                    >
                      <option value="Draft">Draft</option>
                      <option value="Quotation">Quotation</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="col-span-3">
                    <label className="block text-gray-700 font-medium mb-2">Email Addresses</label>
                    <div className="flex gap-2 mb-2">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={emailInput}
                          onChange={(e) => setEmailInput(e.target.value)}
                          onKeyDown={handleEmailKeyDown}
                          placeholder="Enter email and press Enter or Add"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                        />
                        {emailError && <p className="text-red-500 text-sm mt-1">{emailError}</p>}
                      </div>
                      <button
                        type="button"
                        onClick={addEmail}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        Add
                      </button>
                    </div>
                    <div className="mt-2">
                      {formData.emails.length === 0 ? (
                        <p className="text-gray-500 text-sm">No email addresses added yet</p>
                      ) : (
                        <div className="space-y-2">
                          {formData.emails.map((email, index) => (
                            <div key={index} className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-lg">
                              <div className="flex items-center gap-2">
                                <span className="text-blue-700 font-medium">{email}</span>
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                  {index === 0 ? 'Primary' : 'Secondary'}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeEmail(index)}
                                className="text-red-500 hover:text-red-700 text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Driver's License Codes Section */}
                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-medium mb-4">Driver's License Codes</label>
                  <div className="space-y-3">
                    {formData.drivers_licenses.length === 0 ? (
                      <p className="text-gray-500 text-sm">No license codes added yet.</p>
                    ) : (
                      formData.drivers_licenses.map((license, index) => {
                        const availableLicenseCodes = getAvailableLicenseCodes(index);
                        return (
                          <div key={index} className="p-3 bg-gray-50 rounded-lg border">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div>
                                <label className="text-sm font-medium text-gray-700">License Code</label>
                                <select
                                  value={license.code}
                                  onChange={(e) => handleDriverLicenseChange(index, 'code', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                >
                                  <option value="">Select Code</option>
                                  {availableLicenseCodes.map(code => (
                                    <option key={code} value={code}>{code}</option>
                                  ))}
                                </select>
                              </div>

                              <div>
                                <label className="text-sm font-medium text-gray-700">Date of First Issue</label>
                                <DateInput
                                    value={license.dateOfFirstIssue}
                                    onChange={(date) => handleDriverLicenseChange(index, 'dateOfFirstIssue', date)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all bg-white"
                                />
                                </div>

                              <div>
                                <label className="text-sm font-medium text-gray-700">Expires On</label>
                                <DateInput
                                    value={license.expiresOn}
                                    onChange={(date) => handleDriverLicenseChange(index, 'expiresOn', date)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all bg-white"
                                />
                                </div>

                            </div>
                            <div className="flex justify-end mt-3">
                              <button
                                type="button"
                                onClick={() => removeDriverLicense(index)}
                                className="px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                              >
                                Remove License
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <button
                      type="button"
                      onClick={addDriverLicense}
                      disabled={!canAddMoreLicenses()}
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                    >
                      + Add Another License
                    </button>
                  </div>
                </div>

                {/* Buttons */}
                <div className="md:col-span-2 flex justify-end gap-4 mt-6 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => navigate('/clients')}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-md"
                  >
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddClientPage;